import React, { useState, useEffect } from "react";
import { Workflow, Agent, Event } from "../../shared/types";
import { workflowApi, agentApi } from "../services/api";

export const WorkflowSettingsView: React.FC = () => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSavingAgent, setIsSavingAgent] = useState<string | null>(null);

  // Load recent workflows for settings
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load recent workflows
        const workflowsResult = await workflowApi.list(1, 10);
        if (workflowsResult.success && workflowsResult.data) {
          const workflows = workflowsResult.data.workflows || [];
          if (workflows.length > 0) {
            const wf = workflows[0] as Workflow;
            setWorkflow(wf);
            
            // Load agents for this workflow
            if (wf.id) {
              const agentsResult = await agentApi.listForWorkflow(wf.id);
              if (agentsResult.success && agentsResult.data) {
                setAgents(agentsResult.data.agents || []);
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to load workflow settings data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSaveAgent = async (agent: Agent) => {
    if (!workflow) return;
    
    setIsSavingAgent(agent.id);
    try {
      const result = await agentApi.updateForWorkflow(
        workflow.id,
        agent.id,
        { 
          objective: agent.objective, 
          prompt: agent.prompt || "" 
        }
      );
      
      if (result.success) {
        // Update local state
        setAgents(prev => 
          prev.map(a => a.id === agent.id ? agent : a)
        );
      }
    } catch (error) {
      console.error("Failed to save agent:", error);
    } finally {
      setIsSavingAgent(null);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-300">Loading workflow settings...</p>
        </div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-4">No workflow found</div>
          <p className="text-gray-400">Create a workflow first to manage its agents.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-white mb-2">Workflow Agents</h2>
          <p className="text-gray-400">Manage agents in workflow: <span className="text-white">{workflow.name}</span></p>
        </div>

        {agents.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No agents in this workflow</h3>
            <p className="text-gray-400">Agents will appear here when the workflow is running.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {agents.map((agent) => (
              <div key={agent.id} className="bg-bg-card border border-border-primary rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{agent.type}</h3>
                    <p className="text-sm text-gray-400">ID: {agent.id}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    agent.status === 'processing' ? 'bg-green-600 text-green-100' :
                    agent.status === 'complete' ? 'bg-blue-600 text-blue-100' :
                    agent.status === 'error' ? 'bg-red-600 text-red-100' :
                    'bg-gray-600 text-gray-100'
                  }`}>
                    {agent.status}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Objective</label>
                    <textarea
                      value={agent.objective}
                      onChange={(e) => {
                        setAgents(prev => 
                          prev.map(a => 
                            a.id === agent.id ? { ...a, objective: e.target.value } : a
                          )
                        );
                      }}
                      className="w-full p-3 text-sm bg-gray-800 text-white rounded border border-gray-600 focus:border-primary-500 focus:outline-none min-h-[100px] resize-y"
                      placeholder="Agent objective..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Prompt</label>
                    <textarea
                      value={agent.prompt || ""}
                      onChange={(e) => {
                        setAgents(prev => 
                          prev.map(a => 
                            a.id === agent.id ? { ...a, prompt: e.target.value } : a
                          )
                        );
                      }}
                      className="w-full p-3 text-sm bg-gray-800 text-white rounded border border-gray-600 focus:border-primary-500 focus:outline-none min-h-[100px] resize-y"
                      placeholder="Agent prompt..."
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => handleSaveAgent(agent)}
                      disabled={isSavingAgent === agent.id}
                      className={`px-4 py-2 text-sm rounded transition-colors ${
                        isSavingAgent === agent.id
                          ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                          : "bg-primary-600 hover:bg-primary-500 text-white"
                      }`}
                    >
                      {isSavingAgent === agent.id ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
